import { useContext, useEffect, useState } from 'react';
import { useScript } from '@magpie/ui-hooks';
import { Skeleton } from '@magpie/ui-core';
import { useApi, useClientSettings, useExpressWidgetError, useWidgetSettings } from '../../hooks';

import type { DeliveryAddressInfo} from '../../typings/basket.types';
import { PaymentMethodType } from '../../typings/basket.types';
import { handleComponentError } from '../../utils';
import CheckoutRedirect from '../../utils/checkout-redirect';
import { CountryCodeMapper } from '../../utils/country-code-mapper';
import type {
    KlarnaAuthorize,
    KlarnaAuthorizePaymentResponse,
    KlarnaLoadPaymentResponse,
} from '../../typings/klarna.types';
import { WidgetContext } from '../../context/WidgetContextProvider/WidgetContextProvider';
import styles from './KlarnaExpressCheckoutButton.module.scss';
import { PAYMENT_TYPES } from '../../typings/payment.types';
import createKlarnaOrderPayload from '../../utils/klarna/createKlarnaOrderPayload';
import { pushPaymentProviderSelectionEvent } from '../../utils/payment-provider-selection-event';
import validateAddress from '../../utils/validateAddress';
import { AuthorizationError, InvalidAddressDetailsError, StockAllocationError } from '../../errors';
import { isInvalidAddressDetailsError } from '../../guards/isInvalidAddressDetailsError';
import { COOKIE_NAMES } from '../../constants';
import { useOutOfStockRedirectionTimer } from '../../hooks/useOutOfStockRedirectionTimer';
import { shouldEnforceAllocationExpiry } from '../../utils/shouldEnforceAllocationExpiry';
import { getKlarnaPaymentsApi } from '../../utils/windowGlobals';

const DEFAULT_KLARNA_SCRIPT_ATTRS = {
    klarnaExpressCheckoutSDKUrl: 'https://x.klarnacdn.net/kp/lib/v1/api.js',
    minimumAcceptablePrice: 35,
    maximumAcceptablePrice: 1000,
};

const KlarnaExpressCheckoutButton = () => {
    const [state, dispatch] = useContext(WidgetContext);
    const { isKlarnaButtonAvailable, activeBasketSummary } = state;
    const { setExpressWidgetError } = useExpressWidgetError();
    const { apiBasePath, authToken } = useWidgetSettings();
    const { expressCheckoutOptions, currency, countryIso, language, useMmSessionId, rootPath } = useClientSettings();
    // Klarna settings are typed as required, but may be missing until client settings are fetched
    const klarnaSettings = expressCheckoutOptions?.klarnaSettings;
    const [isSessionActive, setIsSessionActive] = useState(false);
    const API = useApi();
    const redirectOnOutOfStockTimer = useOutOfStockRedirectionTimer();

    const klarnaOptions = {
        klarnaExpressCheckoutSDKUrl:
            klarnaSettings?.klarnaExpressCheckoutSDKUrl ?? DEFAULT_KLARNA_SCRIPT_ATTRS.klarnaExpressCheckoutSDKUrl,
        klarnaExpressCheckoutClientKey: klarnaSettings?.klarnaExpressCheckoutClientKey,
        minimumAcceptablePrice:
            klarnaSettings?.minimumAcceptablePrice ?? DEFAULT_KLARNA_SCRIPT_ATTRS.minimumAcceptablePrice,
        maximumAcceptablePrice:
            klarnaSettings?.maximumAcceptablePrice ?? DEFAULT_KLARNA_SCRIPT_ATTRS.maximumAcceptablePrice,
    };

    const sdkLoadingStatus = useScript(klarnaOptions.klarnaExpressCheckoutSDKUrl);

    const isKlarnaInitialized =
        sdkLoadingStatus === 'ready' && isKlarnaButtonAvailable === true && activeBasketSummary !== null;

    const setIsKlarnaButtonAvailable = (available: boolean | null) => {
        dispatch({
            type: 'SET_IS_KLARNA_BUTTON_AVAILABLE',
            payload: available,
        });
    };

    const setupPaymentSessionTokenCookie = (paymentSesssionToken: string) => {
        const cookieDomain = `${rootPath.replace('https://', '').replace('www.', '')}`;
         
        document.cookie = `${COOKIE_NAMES.KLARNA_PAYMENT_CLIENT_TOKEN}=${paymentSesssionToken};domain=${cookieDomain};path=/;`;
    };

    useEffect(() => {
        if (isKlarnaInitialized) {
            try {
                const klarna = getKlarnaPaymentsApi();
                klarna.Payments.Buttons.init({
                    client_key: klarnaOptions.klarnaExpressCheckoutClientKey,
                });
                klarna.Payments.Buttons.load(
                    {
                        container: '#klarnaExpressContainer',
                        theme: 'default',
                        shape: 'default',
                        locale: language,
                        on_click: async (authorize: KlarnaAuthorize) => {
                            if (isSessionActive) return;
                            setIsSessionActive(true);
                            pushPaymentProviderSelectionEvent('KlarnaExpress', useMmSessionId);

                            const klarnaAuthorizePayload = createKlarnaOrderPayload(
                                activeBasketSummary,
                                countryIso,
                                currency,
                            );

                            try {
                                const allocationResult = await API.allocate(
                                    activeBasketSummary.id,
                                    PAYMENT_TYPES.KLARNA,
                                );

                                if (shouldEnforceAllocationExpiry(allocationResult, expressCheckoutOptions)) {
                                    redirectOnOutOfStockTimer.enableUntil(allocationResult);
                                }
                            } catch (error) {
                                setIsSessionActive(false);

                                if (error instanceof AuthorizationError) {
                                    handleComponentError(error, setExpressWidgetError);
                                    return;
                                }

                                handleComponentError(new StockAllocationError(''), setExpressWidgetError);
                                return;
                            }

                            authorize(
                                {
                                    collect_shipping_address: true,
                                    auto_finalize: false,
                                },
                                klarnaAuthorizePayload,
                                async (authResult: KlarnaAuthorizePaymentResponse) => {
                                    try {
                                        if (authResult.approved) {
                                            const { collected_shipping_address: addressData } = authResult;
                                            const deliveryAddress: DeliveryAddressInfo = {
                                                firstName: addressData.given_name,
                                                lastName: addressData.family_name,
                                                phone: addressData.phone,
                                                address1: addressData.street_address,
                                                zipCode: addressData.postal_code,
                                                city: addressData.city,
                                                countryId: CountryCodeMapper.GetCountryId(addressData.country),
                                            };
                                            const validateAddressResult = validateAddress(deliveryAddress);
                                            if (!validateAddressResult.isValid) {
                                                const message = validateAddressResult.errors.join(' ');
                                                throw new InvalidAddressDetailsError(message);
                                            }

                                            let emailAddress = addressData.email;
                                            if (authToken) {
                                                const user = await API.getUser();
                                                
                                                if (user.email) {
                                                    emailAddress = user.email;
                                                }
                                            }

                                            await API.updateCustomerEmail(
                                                emailAddress,
                                                activeBasketSummary.number,
                                                activeBasketSummary.id,
                                            );
                                            await API.updateDeliveryDetails(deliveryAddress, activeBasketSummary.id);

                                            setupPaymentSessionTokenCookie(authResult.client_token);

                                            CheckoutRedirect.RedirectToCheckout(
                                                apiBasePath,
                                                activeBasketSummary.id,
                                                PAYMENT_TYPES.KLARNA,
                                            );
                                        } else if (
                                            authResult.show_form === true
                                        ) {
                                            redirectOnOutOfStockTimer.disable();
                                        } else {
                                            redirectOnOutOfStockTimer.disable();
                                            throw new Error(
                                                `Unexpected Klarna response when authorizing payment session: ${JSON.stringify(
                                                    authResult,
                                                )}`,
                                            );
                                        }
                                    } catch (error) {
                                        if (!isInvalidAddressDetailsError(error)) {
                                            redirectOnOutOfStockTimer.disable();
                                            setIsKlarnaButtonAvailable(false);
                                        }
                                        handleComponentError(error, setExpressWidgetError);
                                    } finally {
                                        setIsSessionActive(false);
                                    }
                                },
                            );
                        },
                    },
                    function load_callback(loadResult: KlarnaLoadPaymentResponse) {
                        setIsKlarnaButtonAvailable(loadResult.show_form);
                    },
                );
            } catch (error) {
                redirectOnOutOfStockTimer.disable();
                setIsKlarnaButtonAvailable(false);
                handleComponentError(error, setExpressWidgetError);
            }
        }
    }, [isKlarnaInitialized, activeBasketSummary]);

    useEffect(() => {
        dispatch({
            type: 'SET_IS_KLARNA_BUTTON_DISPLAYED',
            payload: isKlarnaInitialized,
        });
    }, [isKlarnaInitialized]);

    useEffect(() => {
        if (!activeBasketSummary) {
            return;
        }
        setIsKlarnaButtonAvailable(true);
        if (
            activeBasketSummary.totalPrice >= klarnaOptions.minimumAcceptablePrice &&
            activeBasketSummary.totalPrice <= klarnaOptions.maximumAcceptablePrice
        ) {
            const fetchPaymentMethods = async () => {
                const data = await API.getActiveBasketPaymentMethods();
                const isKlarnaPayAllowed = data.some(
                    (m) => m.paymentMethodType === PaymentMethodType.KlarnaPay && m.isAllowed,
                );
                setIsKlarnaButtonAvailable(isKlarnaPayAllowed);
            };
            void fetchPaymentMethods();
        } else {
            setIsKlarnaButtonAvailable(false);
        }
    }, [activeBasketSummary]);

    return (
        <>
            {isKlarnaButtonAvailable === true && !isKlarnaInitialized && (
                <div data-testid="klarnaSkeletonTestId">
                    <Skeleton color="#ffe5ea" height="55px" rounded="sm" width="100%" />
                </div>
            )}
            {isKlarnaButtonAvailable === true && isKlarnaInitialized && (
                <div className={isSessionActive ? styles.klarnaSessionActive : undefined}>
                    <div
                        id="klarnaExpressContainer"
                        data-testid="klarnaExpressCheckoutContainer"
                        className={styles.klarnaExpressCheckoutButton}
                    />
                </div>
            )}
        </>
    );
};

export default KlarnaExpressCheckoutButton;
